import { useQuery } from "@tanstack/react-query";
import { generateQueryParams } from "nhb-toolbox";
import type { QueryObject } from "nhb-toolbox/object/types";
import { HTTPResponse, httpService } from "src/Helpers/utils/axios";
import type { TQueryTag } from "src/Helpers/utils/constants";

/** * Interface for query options. */
export interface IQueryOptions {
	/** - API endpoint URL */
	endPoint: string;
	/** - Unique key(s) for the query */
	queryKeys: (TQueryTag | string | number)[];
	/** - Optional: allows conditional fetching. Defaults to `true` */
	enabled?: boolean;
	/** - Optional params for the API request */
	params?: QueryObject;
}

/**
 * * Custom hook to handle API queries using tanstack-query.
 *
 * @template T - Type of the response data.
 * @param options - Options for the query, including URL, query key, enabled flag, and params.
 * @param options.url - API endpoint URL.
 * @param options.queryKey - Unique key for the query.
 * @param options.enabled - Optional: allows conditional fetching.
 * @returns `useQuery` hook for handling API queries.
 */
export const useGetQueryWithTotal = <T,>(
	options: IQueryOptions & { params?: QueryObject }
) => {
	const { endPoint, queryKeys, enabled = true, params } = options || {};
	// Include params in the query key to trigger refetch when they change
	const fullQueryKey = [...queryKeys, params];
	const formattedEndpoint = endPoint + generateQueryParams(params);

	return useQuery<HTTPResponse<T>, Error>({
		queryKey: fullQueryKey,
		queryFn: async () => {
			const res = await httpService.get<T>({ url: formattedEndpoint });
			return res;
		},
		enabled,
	});
};
